<template>
  <tile>
    <circle-loader v-if="gettingData || loading" :inverse="true" />
    <step v-else class="stepBtns-flex-end">
      <template #header> How Much Do You Need for Your Selected Term? </template>
      <p class="disclaimer">
        These funds can be used for your school's total cost of attendance which typically includes
        tuition, fees, books, room & board, food & transportation.
      </p>
      <div class="estimate-calculator">
        <div class="description" v-show="!displayEstimateCalculator">
          <p>
            Amount for
            <b>
              {{
                store.state.appData?.application?.schoolConfiguration?.academicTerm
                  ? store.state.appData?.application?.schoolConfiguration?.academicTerm
                  : "Not Yet Selected"
              }}
            </b>

            {{ combinedTerms ? "combined" : "" }}
          </p>

          <a @click="toggleCalculator">How much do I need?</a>
        </div>
        <div class="calculator-tooltip" :class="{ open: displayEstimateCalculator }">
          <estimate-calculator
            :term="term || ''"
            :application="store.state.appData.application"
            @useThisAmount="useThisAmount($event)"
            @close="toggleCalculator"
          />
        </div>
      </div>
      <base-form-field
        v-model="amount"
        :schema="schema"
        :compare="approvedLoanAmount"
        :minimum="minimumAmount"
        @update:modelValue="handleAmountChange"
        :disabled="updatingApp"
        ><template #label>
          <div class="estimate-calculator">
            <div class="description" v-show="displayEstimateCalculator">
              <p>
                Amount for
                <b>
                  {{
                    store.state.appData?.application?.schoolConfiguration?.academicTerm
                      ? store.state.appData?.application?.schoolConfiguration?.academicTerm
                      : "Not Yet Selected"
                  }}
                </b>

                {{ combinedTerms ? "combined" : "" }}
              </p>
            </div>
          </div>
        </template>
      </base-form-field>
      <div class="hint" v-if="productTypeId === ProductIds.LOC">(If you are unsure, enter 0.)</div>
      <div v-if="disbursements.length > 0" class="estimated-disbursements">
        <p>Estimated disbursements to be sent to your school:</p>
        <div class="disbursements">
          <div v-for="(disbursement, index) in disbursements" :key="index" class="disbursement">
            <p class="amount">${{ formatNumber(disbursement.amount) }}</p>
            <p class="term">
              {{ disbursement.term }}
            </p>
          </div>
        </div>
      </div>
      <div class="info-tile">
        <icon icon="info" />
        <template v-if="productTypeId === ProductIds.PEL">
          <p>
            Your maximum loan amount is
            <b>{{ approvedLoanmountDisplay }}</b>
          </p>
        </template>
      </div>

      <template #actions>
        <next-button
          :disabled="loading"
          @click="closeModal"
          text="Cancel"
          class="cancel-btn"
        ></next-button>
        <next-button
          :loading="loading || updatingApp"
          :disabled="!schema.valid || loading || updatingApp"
          @click="goToRateOptions"
          text="Save & Continue"
        />
      </template>
    </step>
  </tile>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import useValidation from "@/use/validation";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Step from "@/components/shared/Step.vue";
import EstimateCalculator from "@/components/loan-configuration/EstimateCalculator.vue";
import { FieldSchema } from "@/models/form";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import { ProductIds } from "@/config/constants";
import Icon from "@/components/shared/Icon.vue";
import Tile from "@/components/shared/Tile.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";
import type { EnrollmentTerm } from "@/models/school";

const store = useStore();
const { formatNumber } = useFormatter();
const { validateBorrowAmount } = useValidation();

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  isCurrent: {
    type: Boolean,
    required: false,
  },
});

const emit = defineEmits<{
  (e: "complete", payload: boolean): void;
  (e: "close", payload: boolean): void;
}>();

const { editingFromConfirmYourLoanDetails } = useApplicationInfo();
const allowZero = computed<boolean>(() => (productTypeId.value === ProductIds.PEL ? false : true));

const requestedAmount = computed<number | null>(() => {
  if (
    amount.value === undefined ||
    amount.value === null ||
    amount.value === "" ||
    isNaN(Number(amount.value))
  ) {
    return null;
  }
  return Number(amount.value);
});

const requestedAmountChanged = computed<boolean>(() => {
  const original = store.state.loanConfigurationModule?.applicationInfo?.loanAmount;

  if (original === requestedAmount.value) {
    return false;
  }

  return true;
});

const productTypeId = computed<number>(() => store.state.appData?.application?.productTypeId);

const minimumAmount = computed<number>(() =>
  allowZero.value ? 0 : store.getters["PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT"],
);

const programMinimumLoanAmount = computed(() => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT"]);

const programMinimumLoanAmountDisplay = computed(
  () => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY"],
);

const approvedLoanmountDisplay = computed<string>(
  () => `$${formatNumber(store?.state?.appData.application.metadata?.approvedLoanAmount) || ""}`,
);

const approvedLoanAmount = computed(
  () => store.state?.appData?.application?.metadata?.approvedLoanAmount,
);

const selectedEnrollmentTerm = computed(() => {
  return store.state.enrollmentTerms?.find(
    (p: EnrollmentTerm) =>
      p.description === store?.state?.appData?.application?.schoolConfiguration?.academicTerm,
  );
});

const term = computed<string>(
  () => store.state.appData.application.schoolConfiguration.academicTerm,
);

const combinedTerms = computed<boolean>(() =>
  (store.state.appData.application.schoolConfiguration.academicTerm || "")?.includes("-"),
);

const disbursements = computed(() => {
  const terms = term.value?.split("-") || [];
  return terms.map((term: string) => {
    return {
      term: term.trim(),
      amount: (Number(amount.value) || 0) / (terms || []).length,
    };
  });
});

const terms = computed<string>(() => {
  const terms = term.value?.split("-") || [];
  let termString = "";
  for (let i = 0; i < terms.length; i++) {
    if (i === 0) {
      termString += terms[i];
    } else if (i > 0 && i < terms.length - 1) {
      termString += `, ${terms[i]}`;
    } else if (i === terms.length - 1) {
      termString += ` and ${terms[i]}`;
    }
  }
  return termString;
});

const gettingData = ref<boolean>(true);
const updatingApp = ref<boolean>(false);
const error = ref<boolean>(false);
const amount = ref<number | undefined | string>(undefined);
const displayEstimateCalculator = ref<boolean>(false);

let schema = reactive({
  name: "amount",
  type: "money",
  subtype: "initialDrawAmount",
  hideLabel: false,
  required: true,
  allowZero: computed(() => allowZero.value).value,
  errorMessage: computed(() =>
    productTypeId.value === ProductIds.PEL
      ? `Minimum loan amount is ${programMinimumLoanAmountDisplay.value}`
      : "An amount to borrow is required.",
  ).value,
  compareField: "loanAmount",
  minimum: computed(() =>
    productTypeId.value === ProductIds.PEL ? programMinimumLoanAmount.value : minimumAmount.value,
  ).value,
  allowDecimals: false,
  productTypeId: computed(() => productTypeId.value).value,
} as FieldSchema);

function useThisAmount(event: number) {
  amount.value = event;
  validateBorrowAmount(schema, amount.value, approvedLoanAmount.value);
}

async function addInitialDrawRequestAmount() {
  updatingApp.value = true;

  if (requestedAmountChanged.value) {
    const response = await store.dispatch("loanConfigurationModule/addOrUpdateInitialDraw", {
      amount: amount.value,
      editingFromConfirmYourLoanDetails: editingFromConfirmYourLoanDetails.value,
    });

    if (response.applicationId) {
      error.value = false;
    } else {
      updatingApp.value = false;
      error.value = true;
    }
  }
}

async function getInitialDrawRequestAmount() {
  if (
    store.state.loanConfigurationModule.applicationInfo.loanAmount &&
    store.state.loanConfigurationModule.applicationInfo.loanAmount > 0
  ) {
    amount.value = store.state.loanConfigurationModule.applicationInfo.loanAmount;
    schema.valid = true;
    validateBorrowAmount(schema, amount.value, approvedLoanAmount.value);
  } else {
    amount.value = undefined;
  }
}

async function goToRateOptions() {
  updatingApp.value = true;

  if (requestedAmountChanged.value) {
    await addInitialDrawRequestAmount();
  }

  emit("complete", true);
}

function toggleCalculator() {
  displayEstimateCalculator.value = !displayEstimateCalculator.value;
}

function closeModal() {
  emit("close", true);
}

function handleAmountChange(amountStr: string) {
  if (amountStr) {
    amount.value = Number(amountStr);
  }
}

watch(
  () => props.isCurrent,
  async (currentValue: boolean) => {
    await getInitialDrawRequestAmount();
    gettingData.value = false;
    updatingApp.value = false;
  },
);
watch(
  () => amount,
  (currentValue: any) => {
    let value = undefined;
    if (currentValue === "0") {
      value = Number(currentValue);
    } else if (currentValue === "") {
      value = undefined;
    } else {
      value = currentValue;
    }

    validateBorrowAmount(schema, value, approvedLoanAmount.value);
  },
);

onMounted(async () => {
  await getInitialDrawRequestAmount();

  store.dispatch(
    "loanConfigurationModule/setSchoolConfiguration",
    store.state?.appData?.application?.schoolConfiguration,
  );

  gettingData.value = false;
});
</script>
<style lang="scss" scoped>
.action-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  flex-wrap: wrap;
}
p {
  font-family: var(--body-font);
  font-weight: 400;
  font-size: 1rem;
}
.estimate-calculator {
  font-family: var(--body-font);
  .description {
    display: flex;
    justify-content: space-between;
    align-items: center;

    p {
      margin: 0;
      width: 65%;
    }

    a {
      margin-bottom: 7px;
      font-family: var(--body-font);
      font-weight: 400;
      font-size: 1rem;
      color: var(--default-link-color);
      text-decoration: underline;
    }
  }

  .calculator-tooltip {
    display: none;
    opacity: 0;
    border-radius: 6px;
    border: 1px solid lightgray;
    transition: opacity 0.6s;
    margin: 1em 0;
    padding: 0 1rem;
    border: 1px solid rgba(var(--theme-color-rgb));

    &.open {
      display: block;
      opacity: 1;
    }
  }
}

.hint {
  font-family: var(--body-font);
  font-size: 0.875rem; // 14px
  font-weight: 400;
  color: #333333;
  font-style: italic;
}

.estimated-disbursements {
  margin-top: 20px;

  p {
    font-family: var(--body-font);
    font-size: 1rem; // 14px
    font-weight: 400;
    color: #333333;
    margin: 0;
  }

  .disbursements {
    font-family: var(--body-font);
    color: #333333;
    font-size: 0.875rem; // 14px
    display: flex;
    padding: 1em 0;
    .disbursement {
      padding: 0 15px;

      &:not(:first-child) {
        border-left: 1px solid lightgray;
      }

      .amount {
        font-size: 1.4rem;
        font-weight: 700;
      }

      .term {
        font-weight: 400;
        font-size: 0.875rem;
      }
    }
  }
}
a.redirect-link {
  color: var(--default-link-color);
  font-family: var(--body-font);
  font-weight: 400;
  font-size: 1rem;
  margin: 0;
  text-align: end;
  text-decoration: underline;
  cursor: pointer;
}

.hide {
  display: none;
}

.info-tile {
  margin: 1.5em 0;
  background: #f2f3f5;
  padding: 1.5em;
  border-radius: 6px;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.3);
  color: #000000;
}
.info-tile .icon {
  color: #000000;
  font-size: 25px;
}

.cancel-btn {
  background: #f2f3f5;
  color: #000000;
  border: solid 1px #f2f3f5;
}
</style>
